import _ from 'lodash';
import React, { useContext } from 'react';
import { Label } from 'semantic-ui-react';
import { ParentContext } from '../../../global/cnr/contexts/ParentContext';
import { isOdd } from '../../../global/common/filtering';
import { uniqueKey } from '../../../global/common/keys';
import { appUserLevels } from '../../../global/enums/globalEnums';
import { handicapModes, handicapRowModes } from './GolferRow';
import { golfEnums } from '../enums/golfEnums';
import { _matchPlayPercs } from '../golfCalcs/calcMatchStatus';
import { useColors } from 'global/cnr/contexts/useHelpers/useColors';

const _lblSize = 'mini'
const _updateDays = 7

/**
 * 
 * @param {object} props 
 * @returns a simple groupd of Labels indicating a golfer's or team's handicap
 */
const GolferHandicapLabelGroup = (props) => {

  const {
    _handicaps,
    _teamHandicaps,
    allowCourseHandicapChange,
    handicapMode,
    handleShowHandicap,
    lowG,
    position,
    roundType,
  } = props ?? {}

  const { getContrastColor, getSplitColors, getSplitColorStyles } = useColors()

  // parentContext
  const parentContext = useContext(ParentContext);
  const { fns } = parentContext ?? {}
  const { appUser_fns } = fns ?? {}

  const allowMods = appUser_fns ? appUser_fns.validateAccess_item(appUserLevels.superAdmin) : false

  const {
    handicapSource,
    handicapIndex,
    tournamentHandicapIndex,
    courseHandicap,
    courseHandicapIndex,
    matchHandicap_alt,
    matchHandicap_alt_perc,
    matchHandicap,
    hasCourseHandicap,
    teebox,
    missing,
    daysSinceUpdate,
    recommendedCourseHandicap,
    recommendedTeeBox,
  } = _handicaps ? _handicaps : {}

  const { ave, sum, lowT, altShot } = _teamHandicaps ? _teamHandicaps : {}

  const golferHcp = (handicapRowMode) => {

    const ghProps = {
      value: null,
      backgroundColor: null,
      allowChange: null,
      isHcp: null,
      handicapRowMode,
      allowClick: false,
      cn: null,
      style: null,
    };

    switch (handicapRowMode) {
      case handicapRowModes.teebox:
        ghProps.value = teebox
        ghProps.title = 'Days Since Update'
        break;

      case handicapRowModes.handicapDays:
        ghProps.value = 'D' + daysSinceUpdate
        ghProps.title = 'Days Since Update'
        break;

      case handicapRowModes.matchHandicap_alt:
        ghProps.value = matchHandicap_alt
        ghProps.title = 'Match Handicap Adjusted'
        break;

      case handicapRowModes.matchHandicap_alt_perc:
        ghProps.value = matchHandicap_alt_perc
        ghProps.title = 'Match Handicap Percentage'
        break;

      case handicapRowModes.matchHandicap_bb_perc:
        ghProps.value = _matchPlayPercs.bestBall
        ghProps.title = 'Match Handicap Percentage'
        break;

      case handicapRowModes.handicapSource:
        ghProps.value = handicapSource
        ghProps.title = 'Handicap Source'
        break;

      case handicapRowModes.ave:
        ghProps.value = ave
        ghProps.title = 'Team Handicap Average'
        break;

      case handicapRowModes.altShot:
        ghProps.value = altShot
        ghProps.title = 'Team Alt Handicap Average'
        break;

      case handicapRowModes.sum:
        ghProps.value = sum
        ghProps.title = 'Team Handicap Sum'
        break;

      case handicapRowModes.handicapIndex:
        ghProps.value = daysSinceUpdate || _.isNumber(daysSinceUpdate) ? daysSinceUpdate : '?'
        ghProps.backgroundColor = 'grey'
        ghProps.title = 'Handicap Index (Out of Date)'
        if ((daysSinceUpdate || _.isNumber(daysSinceUpdate)) && daysSinceUpdate <= _updateDays) {
          ghProps.backgroundColor = 'green'
          ghProps.title = 'Handicap Index'
        }
        ghProps.value = handicapIndex ? handicapIndex : missing
        ghProps.allowChange = allowMods
        ghProps.icon = 'check'
        ghProps.allowClick = true
        break;

      case handicapRowModes.tournamentHandicapIndex:
        ghProps.value = daysSinceUpdate || _.isNumber(daysSinceUpdate) ? daysSinceUpdate : '?'
        if ((daysSinceUpdate || _.isNumber(daysSinceUpdate)) && daysSinceUpdate <= _updateDays) {
          ghProps.backgroundColor = 'green'
        }
        if (handicapIndex !== tournamentHandicapIndex) {
          ghProps.backgroundColor = 'purple'
        }
        ghProps.value = tournamentHandicapIndex ? tournamentHandicapIndex : missing
        ghProps.allowChange = allowMods
        ghProps.title = 'Tournament Handicap Index'
        ghProps.allowClick = true
        break;

      case handicapRowModes.courseHandicap:
        ghProps.value = courseHandicap ? courseHandicap : '?'
        ghProps.style = getSplitColorStyles({ backgroundColor: teebox })
        getSplitColors({ backgroundColor: teebox, colorProps: ghProps })
        ghProps.allowChange = allowMods && allowCourseHandicapChange
        ghProps.title = 'Course Handicap'
        ghProps.allowClick = true
        break;

      case handicapRowModes.matchHandicap:
        ghProps.value = matchHandicap ? matchHandicap : '-'
        ghProps.title = 'Match Handicap'
        break;

      case handicapRowModes.recommendedCourseHandicap:
        ghProps.value = recommendedCourseHandicap ? recommendedCourseHandicap : '-'
        ghProps.title = recommendedTeeBox || 'Recommended Course Handicap'
        ghProps.cn = 'rch'
        ghProps.style = getSplitColorStyles({ backgroundColor: recommendedTeeBox })
        getSplitColors({ color: recommendedTeeBox, colorProps: ghProps })
        ghProps.allowClick = true
        break;
      default:
      // nothing
    }


    return <div><Label
      className={(ghProps.allowChange ? 'ac' : ghProps?.cn)}
      key={uniqueKey('glfl', handicapRowMode)}
      size={_lblSize}
      title={ghProps.title}
      style={ghProps.style ? { ...ghProps.style } : { backgroundColor: ghProps.backgroundColor, border: `2px solid ${ghProps.borderColor}`, color: getContrastColor(ghProps.backgroundColor) }}
      onClick={(e) => { ghProps.allowClick && handleShowHandicap && handleShowHandicap(e, ghProps.handicapRowMode) }}>
      {ghProps.value}
    </Label></div>
  }

  const hcpLabelGroup = () => {

    let cn = 'golfer-hcps label-group'
    const odd = isOdd(position)
    let lbls = [];

    switch (handicapMode) {
      case handicapModes.event:
        lbls = [
          golferHcp(handicapRowModes.handicapSource),
          golferHcp(handicapRowModes.handicapIndex),
        ]
        cn += ' l2'
        break;

      case handicapModes.match:
        switch (roundType) {
          case golfEnums.roundTypes.alternateShot:
          case golfEnums.roundTypes.alternateShot1:
          case golfEnums.roundTypes.alternateShot2:
            lbls = [
              golferHcp(handicapRowModes.tournamentHandicapIndex),
              golferHcp(handicapRowModes.courseHandicap),
              golferHcp(handicapRowModes.matchHandicap_alt_perc),
              golferHcp(handicapRowModes.matchHandicap_alt)
            ]
            break;

          case golfEnums.roundTypes.bestBall:
          case golfEnums.roundTypes.bestBall1:
          case golfEnums.roundTypes.bestBall2:
            lbls = [
              golferHcp(handicapRowModes.tournamentHandicapIndex),
              golferHcp(handicapRowModes.courseHandicap),
              golferHcp(handicapRowModes.matchHandicap_bb_perc),
              golferHcp(handicapRowModes.matchHandicap),
            ]
            break;

          default:
            lbls = [
              golferHcp(handicapRowModes.tournamentHandicapIndex),
              golferHcp(handicapRowModes.courseHandicap),
              golferHcp(handicapRowModes.matchHandicap),
            ]
        }

        cn += ' l2'
        break;

      case handicapModes.teamMatch:
        lbls = [
          golferHcp(handicapRowModes.sum),
          golferHcp(handicapRowModes.ave),
          golferHcp(handicapRowModes.altShot),
        ]
        cn += ' l2'
        break;

      case handicapModes.tournament:
        lbls = [
          golferHcp(handicapRowModes.tournamentHandicapIndex),
        ]

        if (courseHandicap) {
          allowMods && lbls.push(golferHcp(handicapRowModes.recommendedCourseHandicap))
          lbls.push(golferHcp(handicapRowModes.courseHandicap))
        } else {
          lbls.push(golferHcp(handicapRowModes.recommendedCourseHandicap))
          lbls.push(golferHcp(handicapRowModes.courseHandicap))
        }

        cn += ' l3'
        break;
      default:
      // nothing
    }
    if (odd) { cn += ' odd' }
    if (lowG) { cn += ' lg' }
    if (lowT) { cn += ' lt' }
    return <div className={cn}>{lbls}</div>
  }

  return hcpLabelGroup()

}

export default GolferHandicapLabelGroup